import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import billingsTaxiUnbilledRouteListingUserPermissionsHelper from "./common/user-permissions/billings-taxi-unbilled-route-listing-user-permission.helper";
import BillingsTaxiUnbilledRouteListingViewMode from "./common/types/billings-taxi-unbilled-route-listing-view-mode";
import ButtonComponent from "../../../../common/components/button/button.component";
import BillingsTaxiUnbilledRouteListingByRailyComponent from "./by-raily/billings-taxi-unbilled-route-listing-by-raily.component";
import BillingsTaxiUnbilledRouteListingByTaxiComponent from "./by-taxi/billings-taxi-unbilled-route-listing-by-taxi.component";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";

type BillingsTaxiUnbilledRouteListingProps = {};

const BillingsTaxiUnbilledRouteListingComponent: FC<
  BillingsTaxiUnbilledRouteListingProps
> = () => {
  const { user, selectedAppLanguage, setBreadcrumbs } = useAppContext();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.billingsTaxiUnbilledRouteListing);

  const userPermissions = useMemo(
    () =>
      billingsTaxiUnbilledRouteListingUserPermissionsHelper.getPermissions(
        user?.roles!
      ),
    []
  );

  const translations = useMemo(
    () =>
      billingsTranslationsHelper.getTaxiUnbilledRouteBillingsListingTranslations(),
    [selectedAppLanguage]
  );

  useEffect(() => {
    const breadcrumbs =
      billingBreadcrumbsHelper.getTaxiUnbilledRouteListingBreadcrumbs();

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const [selectedViewMode, setSelectedViewMode] =
    useState<BillingsTaxiUnbilledRouteListingViewMode | null>(null);

  const ChangeViewToRailyButton = useMemo(
    () => (
      <ButtonComponent
        type="brand"
        onClick={() =>
          setSelectedViewMode(BillingsTaxiUnbilledRouteListingViewMode.RAILY)
        }
        title={translations.header.changeViewToRailyButtonTitle}
      >
        {translations.header.changeViewToRailyButtonText}
      </ButtonComponent>
    ),
    [translations]
  );

  const ChangeViewToCargoButton = useMemo(
    () => (
      <ButtonComponent
        type="brand"
        onClick={() =>
          setSelectedViewMode(BillingsTaxiUnbilledRouteListingViewMode.TAXI)
        }
        title={translations.header.changeViewToTaxiButtonTitle}
      >
        {translations.header.changeViewToTaxiButtonText}
      </ButtonComponent>
    ),
    [translations]
  );

  const viewChangeButtonOptions = useMemo(
    () => [
      {
        viewMode: BillingsTaxiUnbilledRouteListingViewMode.RAILY,
        buttonOptions: {
          button: ChangeViewToCargoButton,
          hasPermission: userPermissions.hasAccessToTaxiView,
        },
      },
      {
        viewMode: BillingsTaxiUnbilledRouteListingViewMode.TAXI,
        buttonOptions: {
          button: ChangeViewToRailyButton,
          hasPermission: userPermissions.hasAccessToRailyView,
        },
      },
    ],
    [userPermissions, ChangeViewToCargoButton, ChangeViewToRailyButton]
  );

  const getViewChangeButtons = useCallback(
    (viewMode: BillingsTaxiUnbilledRouteListingViewMode) => {
      const buttonOptionsForSelectedViewMode = viewChangeButtonOptions
        .filter((option) => option.viewMode === viewMode)
        .map((option) => option.buttonOptions);

      return buttonOptionsForSelectedViewMode
        .filter((option) => option.hasPermission)
        .map((option) => option.button);
    },
    [viewChangeButtonOptions]
  );

  const viewOptions = useMemo(
    () => [
      {
        mode: BillingsTaxiUnbilledRouteListingViewMode.RAILY,
        component: (
          <BillingsTaxiUnbilledRouteListingByRailyComponent
            actionButtons={getViewChangeButtons(
              BillingsTaxiUnbilledRouteListingViewMode.RAILY
            )}
          />
        ),
        hasPermission: userPermissions.hasAccessToRailyView,
      },
      {
        mode: BillingsTaxiUnbilledRouteListingViewMode.TAXI,
        component: (
          <BillingsTaxiUnbilledRouteListingByTaxiComponent
            actionButtons={getViewChangeButtons(
              BillingsTaxiUnbilledRouteListingViewMode.TAXI
            )}
          />
        ),
        hasPermission: userPermissions.hasAccessToTaxiView,
      },
    ],
    [getViewChangeButtons]
  );

  const getPossibleViewOptions = useCallback(() => {
    return viewOptions.filter((option) => option.hasPermission);
  }, []);

  const possibleViewOptions = useMemo(
    () => getPossibleViewOptions(),
    [getPossibleViewOptions]
  );

  useEffect(() => {
    if (possibleViewOptions.length === 0) {
      return;
    }
    setSelectedViewMode(possibleViewOptions[0].mode);
  }, [possibleViewOptions]);

  const SelectedViewComponent = useMemo(
    () =>
      viewOptions.find((option) => option.mode === selectedViewMode)?.component,
    [selectedViewMode, viewOptions]
  );

  if (!SelectedViewComponent) {
    return null;
  }

  return SelectedViewComponent;
};

export default BillingsTaxiUnbilledRouteListingComponent;
